/** Pure page-layout math for the PDF step-by-step instructions (#712), kept free
 *  of jsPDF / paper imports so it's unit-testable. The renderer in
 *  `pdfExporter.ts` measures each step's wrapped body, then uses these to place
 *  the steps so none spills past the footer. */

/** Height (mm) a rendered instruction step occupies: bold title (6), its wrapped
 *  body lines (5 each), an optional "Tools:" line (6), and the trailing gap (4).
 *  Slightly over-reserves vs. the actual line spacing, so a step laid out to fit
 *  never runs past the footer. */
export function instructionStepHeight(
  bodyLineCount: number,
  hasTools: boolean,
): number {
  return 6 + bodyLineCount * 5 + 4 + (hasTools ? 6 : 0) + 4;
}

/** Assign each instruction step a page index + y so no step spills past the
 *  footer. Steps flow from `top` on the first page and from `pageTop` after a
 *  break; a step is kept whole, breaking to a fresh page when it won't fit in the
 *  remaining space. A step taller than a whole page can't be split — it still
 *  starts at the top of a page rather than forcing an endless break. */
export function layoutInstructionSteps(
  heights: number[],
  geom: { top: number; bottom: number; pageTop: number },
): Array<{ page: number; y: number }> {
  const out: Array<{ page: number; y: number }> = [];
  const pageCapacity = geom.bottom - geom.pageTop;
  let page = 0;
  let y = geom.top;
  for (const h of heights) {
    // Don't break when already at a FRESH page top (a giant step can't be split).
    const atFreshPageTop = y === geom.pageTop;
    if (!atFreshPageTop && y + Math.min(h, pageCapacity) > geom.bottom) {
      page += 1;
      y = geom.pageTop;
    }
    out.push({ page, y });
    y += h;
  }
  return out;
}
